import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, Sparkles, Clock } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import coinBag from "@/assets/coin-bag.png";

const TOTAL_DAYS = 30;

const rewardForDay = (day: number) => ({
  day,
  coins: 10 + (day - 1) * 5,
});

const nextDayNumber = (day: number) => (day >= TOTAL_DAYS ? 1 : day + 1);

interface DailyState {
  canClaim: boolean;
  currentDay: number; // 1..30 — the day to claim NEXT
  loading: boolean;
  nextAvailableAt: Date | null;
  streak: number;
}

const formatCountdown = (ms: number) => {
  if (ms <= 0) return "Available now";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const DailyRewards = () => {
  const { user, refreshBalance } = useApp();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<DailyState>({
    canClaim: false,
    currentDay: 1,
    loading: true,
    nextAvailableAt: null,
    streak: 0,
  });
  const [justClaimed, setJustClaimed] = useState<{ coins: number; diamonds: number; day: number } | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [missedNotice, setMissedNotice] = useState<{ days: number; previousStreak: number } | null>(null);

  // Live countdown tick
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchState = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("daily_rewards" as any)
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    if (!data) {
      setState({ canClaim: true, currentDay: 1, loading: false, nextAvailableAt: null, streak: 0 });
      return;
    }

    const rec = data as any;
    const lastClaimedAt = rec.last_claimed_at
      ? new Date(rec.last_claimed_at)
      : rec.updated_at
        ? new Date(rec.updated_at)
        : null;
    const hasEverClaimed = !!rec.last_claim_date;

    if (!hasEverClaimed || !lastClaimedAt) {
      setState({ canClaim: true, currentDay: 1, loading: false, nextAvailableAt: null, streak: 0 });
      return;
    }

    const nextAt = new Date(lastClaimedAt.getTime() + 24 * 3600 * 1000);
    const secondsSince = (Date.now() - lastClaimedAt.getTime()) / 1000;

    let displayDay = rec.current_day_state ?? 1;
    if (displayDay > TOTAL_DAYS) displayDay = 1;

    setState({
      canClaim: secondsSince >= 86400,
      currentDay: displayDay,
      loading: false,
      nextAvailableAt: nextAt,
      streak: rec.streak_count ?? 0,
    });
  }, [user]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  // Check whether the user missed days since their last claim — show a one-time
  // notice so they know exactly why their streak reset.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase.rpc as any)("check_streak_status", { p_user_id: user.id });
      if (cancelled || !data) return;
      const missed = (data as any).missed_days ?? 0;
      const prev = (data as any).previous_streak ?? 0;
      const key = `talklot_streak_notice_${user.id}_${missed}_${prev}`;
      if (missed > 0 && prev > 0) {
        try {
          if (localStorage.getItem(key) === "1") return;
          localStorage.setItem(key, "1");
        } catch {}
        setMissedNotice({ days: missed, previousStreak: prev });
        // Email only when the BROKEN streak was exactly 3 days or exactly 7 days.
        if ((prev === 3 || prev === 7) && user.email && !user.isAnonymous) {
          try {
            void supabase.functions.invoke("send-email", {
              body: {
                type: "streak_broken",
                to: user.email,
                user_id: user.id,
                idempotency_key: `streak-${user.id}-${prev}-${Date.now().toString().slice(0, 8)}`,
                data: { streak: prev },
              },
            });
          } catch {}
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Auto-open popup when reward is ready (after login).
  // Delay extra long if the Night Fest popup is queued, so the fest popup shows first.
  useEffect(() => {
    if (user && state.canClaim && !state.loading) {
      // Lazy import to avoid circular deps
      let delay = 800;
      try {
        // Check if fest popup is queued — if so, wait longer
        const sessionKey = (() => {
          const now = new Date();
          const day = now.getDay();
          const hr = now.getHours();
          if (day >= 1 && day <= 5 && hr >= 20 && hr < 22) {
            return `talklot_fest_popup_shown_${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}-fest`;
          }
          return "talklot_fest_popup_shown_intro";
        })();
        if (localStorage.getItem(sessionKey) !== "1") delay = 3500;
      } catch {}
      const t = setTimeout(() => setIsOpen(true), delay);
      return () => clearTimeout(t);
    }
  }, [user, state.canClaim, state.loading]);

  // Re-evaluate availability live
  useEffect(() => {
    if (state.nextAvailableAt && !state.canClaim) {
      if (now >= state.nextAvailableAt.getTime()) {
        setState((s) => ({ ...s, canClaim: true }));
      }
    }
  }, [now, state.nextAvailableAt, state.canClaim]);

  const handleClaim = async () => {
    console.log("Claim button clicked", { canClaim: state.canClaim, userId: user?.id });
    if (!user) {
      toast({ title: "Sign in required", variant: "destructive" });
      return;
    }
    if (!state.canClaim) {
      toast({ title: "Come back later", description: "Your next reward isn't ready yet." });
      return;
    }
    const { data, error } = await supabase.rpc("claim_daily_reward" as any, { p_user_id: user.id });
    if (error) {
      console.log("claim_daily_reward error:", error);
      toast({ title: "Couldn't claim", description: error.message, variant: "destructive" });
      return;
    }
    const result = data as any;
    console.log("claim_daily_reward result:", result);
    if (!result?.success) {
      toast({ title: "Come back later", description: result?.error || "Already claimed today." });
      fetchState();
      return;
    }
    await refreshBalance();
    setJustClaimed({ coins: result.coins, diamonds: result.diamonds, day: result.day });
    setShowConfetti(true);
    const nextAt = result.next_available_at ? new Date(result.next_available_at) : new Date(Date.now() + 86400000);
    setState((s) => ({
      ...s,
      canClaim: false,
      streak: result.streak ?? s.streak,
      currentDay: result.day >= TOTAL_DAYS ? 1 : result.day + 1,
      nextAvailableAt: nextAt,
    }));
    setTimeout(() => {
      setJustClaimed(null);
      setShowConfetti(false);
    }, 2800);
  };

  const reward = useMemo(() => rewardForDay(state.currentDay), [state.currentDay]);
  const countdownMs = state.nextAvailableAt ? state.nextAvailableAt.getTime() - now : 0;

  if (!user || state.loading) return null;

  return (
    <>
      <AnimatePresence>
        {missedNotice && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mx-4 mb-2 relative z-10 w-[calc(100%-2rem)] rounded-2xl px-4 py-3 border border-destructive/30 bg-destructive/10 flex items-start gap-3"
          >
            <span className="text-xl">😔</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-display font-bold text-foreground leading-tight">
                You missed {missedNotice.days} {missedNotice.days === 1 ? "day" : "days"}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Your {missedNotice.previousStreak}-day streak has been reset. Claim today to start a new streak!
              </p>
            </div>
            <button onClick={() => setMissedNotice(null)} className="p-1 -m-1 text-muted-foreground"><X className="w-4 h-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Banner trigger */}
      {state.canClaim && (
        <motion.button
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsOpen(true)}
          className="mx-4 mb-2 relative z-10 w-[calc(100%-2rem)]"
        >
          <div
            className="rounded-2xl px-5 py-4 overflow-hidden border border-accent/25"
            style={{ background: "linear-gradient(135deg, hsl(270, 40%, 8%), hsl(330, 50%, 10%))" }}
          >
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-accent/[0.06] to-transparent animate-shimmer"
              style={{ backgroundSize: "200% 100%" }}
            />
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-11 h-11 rounded-xl gradient-premium flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-display font-bold text-foreground leading-tight">
                  Day {state.currentDay} Reward Ready!
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  +{reward.coins} coins
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <img src={coinBag} alt="coins" className="w-9 h-9 object-contain" />
              </div>
            </div>
          </div>
        </motion.button>
      )}

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setIsOpen(false)}
          >
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {Array.from({ length: 30 }).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{
                      y: -20,
                      x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 400),
                      opacity: 1,
                      scale: 0,
                    }}
                    animate={{ y: 700, opacity: 0, scale: 1, rotate: Math.random() * 720 }}
                    transition={{ duration: 1.5 + Math.random(), delay: Math.random() * 0.4 }}
                    className={`absolute w-3 h-3 rounded-full ${
                      i % 5 === 0
                        ? "bg-yellow-400"
                        : i % 5 === 1
                        ? "bg-blue-400"
                        : i % 5 === 2
                        ? "bg-pink-400"
                        : i % 5 === 3
                        ? "bg-emerald-400"
                        : "bg-purple-400"
                    }`}
                  />
                ))}
              </div>
            )}

            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl p-6 border border-accent/20 shadow-2xl max-h-[90vh] flex flex-col"
              style={{ background: "linear-gradient(180deg, hsl(270, 30%, 8%), hsl(330, 20%, 6%), hsl(0, 0%, 4%))" }}
            >
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Gift className="w-6 h-6 text-accent flex-shrink-0" />
                  <h3 className="font-display font-bold text-foreground text-lg">Daily Rewards</h3>
                  {state.streak > 0 && (
                    <span className="ml-1 px-2 py-0.5 rounded-full bg-accent/15 text-accent text-[10px] font-bold whitespace-nowrap">
                      🔥 {state.streak}d
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Current Day + Next Day cards */}
              <div className="grid grid-cols-2 gap-3 mb-4 flex-shrink-0">
                {/* Current Day */}
                <div
                  className={`rounded-2xl p-4 text-center border-2 ${
                    state.canClaim
                      ? "border-accent bg-accent/10 shadow-lg shadow-accent/20"
                      : "border-border/30 bg-muted/10"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                    Current Day
                  </p>
                  <p className="text-xl font-display font-extrabold text-foreground mt-1">
                    Day {state.currentDay}
                  </p>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center justify-center gap-1.5">
                      <img src={coinBag} alt="" className="w-5 h-5 object-contain" />
                      <span className="text-sm font-bold text-foreground">{reward.coins} Coins</span>
                    </div>
                  </div>
                  {state.canClaim && (
                    <span className="mt-2 inline-block px-2 py-0.5 rounded-full bg-accent/20 text-accent text-[9px] font-bold uppercase tracking-wider">
                      Ready
                    </span>
                  )}
                </div>

                {/* Next Day */}
                {(() => {
                  const nextNum = nextDayNumber(state.currentDay);
                  const nextReward = rewardForDay(nextNum);
                  return (
                    <div className="rounded-2xl p-4 text-center border-2 border-border/20 bg-muted/5 opacity-80">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                        Next Day
                      </p>
                      <p className="text-xl font-display font-extrabold text-foreground/80 mt-1">
                        Day {nextNum}
                      </p>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-center gap-1.5">
                          <img src={coinBag} alt="" className="w-5 h-5 object-contain opacity-80" />
                          <span className="text-sm font-bold text-foreground/80">{nextReward.coins} Coins</span>
                        </div>
                      </div>
                      <span className="mt-2 inline-block px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground text-[9px] font-bold uppercase tracking-wider">
                        Tomorrow
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Footer / claim button */}
              <div className="relative flex-shrink-0">
                {justClaimed ? (
                  <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="text-center py-4">
                    <p className="text-lg font-display font-bold text-emerald-400">
                      🎉 Day {justClaimed.day} Claimed!
                    </p>
                    <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-4">
                      <span className="flex items-center gap-1.5">
                        <img src={coinBag} alt="" className="w-6 h-6 object-contain" />
                        <span className="font-bold text-foreground">+{justClaimed.coins} Coins</span>
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      Next reward in 24 hours
                    </p>
                  </motion.div>
                ) : state.canClaim ? (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleClaim}
                    className="w-full py-4 rounded-2xl gradient-premium font-display font-bold text-base text-primary-foreground flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Gift className="w-5 h-5" />
                    Claim Day {state.currentDay} · +{reward.coins} Coins
                  </motion.button>
                ) : (
                  <div className="text-center py-4 rounded-2xl bg-muted/10 border border-border/10">
                    <p className="text-xs text-muted-foreground font-medium mb-1.5 flex items-center justify-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      Next reward in
                    </p>
                    <p className="text-2xl font-display font-bold text-accent tabular-nums">
                      {formatCountdown(countdownMs)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Day {state.currentDay} · +{reward.coins} Coins
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DailyRewards;
